/**
 * `EmailPane` — the EMAIL tab body: the agent's mailbox, read-only.
 *
 * ## Layout (operator direction 2026-07-22)
 * Three elements, Outlook-shaped but deliberately simpler:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │ [Inbox] [Sent] [Spam]   ← mailbox tabs + counts       │
 *   ├────────────────────────┬─────────────────────────────┤
 *   │ message list           │ message viewer              │
 *   │ (selected box)         │ (selected message)          │
 *   └────────────────────────┴─────────────────────────────┘
 *
 * It is width-driven, measured on THIS container (not the viewport — the pane is
 * user-resizable between 35–65 vw, so a viewport breakpoint would lie):
 *   - `wide`   (≥ 860px) → list beside viewer — the Outlook shape, and the
 *                          target: the viewer is ALWAYS on the page, never a
 *                          second screen you navigate to;
 *   - `medium` (≥ 620px) → nav + list ABOVE / viewer BELOW (reading pane at the
 *                          bottom) — still both visible, just stacked;
 *   - `narrow`           → single column; the list ⇄ viewer swap is the only
 *                          honest option left.
 *
 * ## Gating
 * `mail.mailboxes` is the presence signal: method-not-found ⇒ the runtime
 * predates `mail.*` → the honest placeholder; `credentialsConfigured:false`
 * comes back as an EMPTY mailbox list → the "no mailboxes" state.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";

import { useMailboxes } from "@/hooks/useMail";
import type { MailEnvelope } from "@/api/lx-mail";
import { AgentChannelPlaceholder } from "@/components/agent/AgentChannelPlaceholder";
import { MailWaitingCue } from "@/components/agent/MailWaitingCue";
import { EmailMailboxNav } from "@/components/agent/EmailMailboxNav";
import { EmailMessageList } from "@/components/agent/EmailMessageList";
import { EmailMessageView } from "@/components/agent/EmailMessageView";
import { cn } from "@/lib/utils";

/** Column-count breakpoints, measured on the pane itself. */
const WIDE_MIN_PX = 860;
const MEDIUM_MIN_PX = 620;

type Density = "narrow" | "medium" | "wide";

function densityFor(width: number): Density {
  if (width >= WIDE_MIN_PX) return "wide";
  if (width >= MEDIUM_MIN_PX) return "medium";
  return "narrow";
}

/** Prefer an inbox-like mailbox as the default selection. */
function pickDefaultMailbox(boxes: { id: string; name: string }[]): string | null {
  if (boxes.length === 0) return null;
  const inbox = boxes.find(
    (m) => /inbox|entrada|caixa de entrada/i.test(m.name) || /inbox/i.test(m.id),
  );
  return (inbox ?? boxes[0]).id;
}

export function EmailPane({ icon, fill = false }: { icon: LucideIcon; fill?: boolean }) {
  const { t } = useTranslation();
  const { mailboxes, gate } = useMailboxes({ enabled: true });
  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);
  const [selectedMsg, setSelectedMsg] = useState<MailEnvelope | null>(null);

  // Container-aware layout: measure OUR width, not the viewport — the focus pane
  // is user-resizable, so a `lg:` breakpoint would misreport a narrow pane on a
  // wide monitor (the same lesson FocusPaneHost learned).
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [gate]);
  const density = densityFor(width);

  const defaultMailbox = useMemo(() => pickDefaultMailbox(mailboxes), [mailboxes]);
  const activeMailbox =
    selectedMailbox && mailboxes.some((m) => m.id === selectedMailbox)
      ? selectedMailbox
      : defaultMailbox;

  const shell = (children: React.ReactNode) => (
    <div
      ref={rootRef}
      data-testid="agent-email-pane"
      data-density={density}
      className={cn("flex min-h-0 flex-col", fill ? "h-full flex-1" : "h-[36rem]")}
    >
      {children}
    </div>
  );

  if (gate === "loading") {
    // `mail.mailboxes` measured 6-15 s (one fresh IMAP session per folder for
    // the counters) — the FIRST wait every visitor hits, so it gets the same
    // escalating cue as the list/viewer, not a bare spinner.
    return shell(
      <div className="flex flex-1 items-center justify-center">
        <MailWaitingCue />
      </div>,
    );
  }

  // A runtime that predates `mail.*` — keep the honest placeholder.
  if (gate === "disabled") {
    return shell(<AgentChannelPlaceholder icon={icon} channelKey="email" />);
  }

  if (mailboxes.length === 0 || !activeMailbox) {
    return shell(
      <AgentChannelPlaceholder
        icon={icon}
        channelKey="email"
        titleKey="agent.channels.email.title"
        bodyKey="agent.channels.email.noMailboxes"
        testId="email-no-mailboxes"
      />,
    );
  }

  const onPickMailbox = (id: string) => {
    setSelectedMailbox(id);
    setSelectedMsg(null);
  };

  const listColumn = (
    <EmailMessageList
      key={activeMailbox}
      mailbox={activeMailbox}
      // The localized label, so a sent-like folder is still recognised when the
      // runtime hands back an opaque id.
      mailboxName={mailboxes.find((m) => m.id === activeMailbox)?.name}
      selectedId={selectedMsg?.messageId ?? null}
      onSelect={setSelectedMsg}
      // Only when the viewer is on screen beside/below the list.
      autoSelectFirst={density !== "narrow"}
    />
  );

  const viewerColumn = selectedMsg ? (
    <EmailMessageView
      key={selectedMsg.messageId}
      messageId={selectedMsg.messageId}
      subjectHint={selectedMsg.subject}
      onBack={() => setSelectedMsg(null)}
      // "Back" only matters where the viewer REPLACED the list (narrow); in the
      // wide/stacked layouts the list never went away.
      showBack={density === "narrow"}
    />
  ) : (
    <div
      data-testid="email-viewer-empty"
      className="flex h-full items-center justify-center bg-cream-50/70 px-6 text-center text-caption text-cream-500 dark:bg-coffee-950/50"
    >
      {t("agent.channels.email.selectToRead")}
    </div>
  );

  const nav = (
    <EmailMailboxNav
      mailboxes={mailboxes}
      active={activeMailbox}
      onSelect={onPickMailbox}
    />
  );
  // Narrow only: a single column that swaps list ⇄ viewer (no room for both).
  const workingColumn = (
    <div className="flex min-h-0 flex-1 flex-col">
      {selectedMsg ? viewerColumn : listColumn}
    </div>
  );

  return shell(
    <>
      {/* Mailboxes are a TAB ROW across the top at every density, so the
          columns below own the full width. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {nav}
        {density === "narrow" ? (
          workingColumn
        ) : density === "wide" ? (
          <div className="flex min-h-0 flex-1 overflow-hidden">
            {/* The LIST is the primary column and must grow. It used to be a
                FIXED 17.5rem while the viewer took every remaining pixel —
                backwards, and the reason senders and subjects were truncated
                on a wide pane. Now both flex, list-favoured, each with a
                min-width so neither can be squeezed into uselessness. */}
            <div className="flex min-h-0 flex-[5] basis-0 min-w-[19rem] flex-col border-r border-cream-200/60 dark:border-coffee-800/60">
              {listColumn}
            </div>
            <div className="flex min-h-0 flex-[4] basis-0 min-w-[16rem] flex-col">
              {viewerColumn}
            </div>
          </div>
        ) : (
          // MEDIUM: not enough width for a second column, but the reading pane
          // should still be visible rather than swapping the list away — so
          // stack it BELOW (the classic "reading pane at bottom").
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-[2] flex-col border-b border-cream-200/60 dark:border-coffee-800/60">
              {listColumn}
            </div>
            <div className="flex min-h-0 flex-[3] flex-col">{viewerColumn}</div>
          </div>
        )}
      </div>

      <p
        data-testid="email-readonly-note"
        className="shrink-0 border-t border-cream-200/60 px-3 py-1 text-[11px] text-coffee-500 dark:border-coffee-800/60 dark:text-cream-500"
      >
        {t("agent.channels.readOnlyNote")}
      </p>
    </>,
  );
}
